import React, { useEffect, useState, Suspense } from 'react';
import { 
   BrowserRouter as Router,
  Routes, 
  Route, 
  Navigate, 
  useLocation,
  useNavigate
} from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';

import { AuthProvider, useAuth } from './hooks/useAuth.jsx';
import { useOfflineSession } from './hooks/useOfflineSession';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import notificationService from './services/notificationService';
import { isInAppBrowser } from './browserDetection';
import InAppBrowserScreen from './components/InAppBrowserScreen';
import BannedScreen from './components/BannedScreen';
import supabaseService from './services/supabaseService';

// Pages
import AuthScreen from './components/AuthScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import VerifyRecoveryScreen from './components/VerifyRecoveryScreen';
import VerifyEmailScreen from './components/VerifyEmailScreen';
import RegistrationScreen from './components/RegistrationScreen';
import AuthVerificationScreen from './components/AuthVerificationScreen';
import ClientMainScreen from './components/ClientMainScreen';
import ProviderMainScreen from './components/ProviderMainScreen';
import ChatScreen from './components/ChatScreen';
import ProvidersListScreen from './components/ProvidersListScreen';
import ContactUsScreen from './components/ContactUsScreen';
import RulesAndTermsScreen from './components/RulesAndTermsScreen';
import PrivacyPolicyScreen from './components/PrivacyPolicyScreen';
import DeleteAccountScreen from './components/DeleteAccountScreen';
import ReceiptUploadScreen from './components/ReceiptUploadScreen';
import ProviderProfileScreen from './components/ProviderProfileScreen';
import ProviderConversationsScreen from './components/ProviderConversationsScreen';
import ProviderBankAccountScreen from './components/ProviderBankAccountScreen';
import MemoryGameScreen from './components/MemoryGameScreen';
import PaymentMethodScreen from './components/PaymentMethodScreen';
import CardPaymentScreen from './components/CardPaymentScreen';
import PaymentSuccessScreen from './components/Paymentsuccessscreen';
import FCMTokenDisplay from './components/FCMTokenDisplay';
import GamesHub from './components/GamesHub';
import IncomingCallManager from './components/IncomingCallManager';

// ✅ استيراد صفحات المحفظة والبطاقة
import MemberCardScreen from './components/MemberCardScreen';
import WalletScreen from './components/WalletScreen';
import WithdrawalRequestScreen from './components/WithdrawalRequestScreen';
import WalletTransactionsScreen from './components/WalletTransactionsScreen';
import RechargeReceiptScreen from './components/RechargeReceiptScreen';

import ProviderPublicProfileScreen from './components/ProviderPublicProfileScreen';
import TestPublicProfileScreen from './components/TestPublicProfileScreen';
import ClientPublicProfileScreen from './components/ClientPublicProfileScreen';
import ReadBookScreen from './components/ReadBookScreen';
import QrScanPage from './components/QrScanPage';
import QrCodeScreen from './components/QrCodeScreen';
import MyLibraryScreen from './components/MyLibraryScreen';
import DigitalProductsScreen from './components/DigitalProductsScreen';
import DigitalProductDetailScreen from './components/DigitalProductDetailScreen';
import StoreScreen from './components/StoreScreen';
import StoreProductDetailScreen from './components/StoreProductDetailScreen';
import StoreCartScreen from './components/StoreCartScreen';
import PersonalQrScreen from './components/PersonalQrScreen';
import QrTransferScreen from './components/QrTransferScreen';
import FoodStoreScreen from './components/FoodStoreScreen';
import FoodProductDetailScreen from './components/FoodProductDetailScreen';
import FoodCartScreen from './components/FoodCartScreen';

const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
    <div className="relative">
      <div className="absolute inset-0 animate-ping rounded-full bg-blue-100 opacity-75"></div>
      <div className="relative animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
    </div>
    <p className="mt-6 text-gray-600 text-sm font-medium">جاري التهيئة...</p>
    <p className="mt-2 text-gray-400 text-xs">قد تستغرق العملية بضع ثوانٍ</p>
  </div>
);

const ErrorScreen = ({ error, onRetry }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
    <div className="text-center max-w-md">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">حدث خطأ غير متوقع</h2>
      <p className="text-gray-600 mb-2">{error || 'حدث خطأ في تحميل التطبيق'}</p>
      <p className="text-gray-500 text-sm mb-8">
        يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى
      </p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onRetry}
          className="flex items-center gap-2 bg-gray-900 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          إعادة المحاولة
        </button>
        <button
          onClick={() => window.location.reload()}
          className="border border-gray-300 text-gray-700 py-2.5 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          تحديث الصفحة
        </button>
      </div>
    </div>
  </div>
);

const PageWrapper = ({ children }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.08, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    if (error.message && error.message.includes('Notification')) {
      const browserInfo = isInAppBrowser();
      if (browserInfo.isInApp) {
        console.warn(`⚠️ Notification error in ${browserInfo.platform} in-app browser`);
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const browserInfo = isInAppBrowser();
      if (browserInfo.isInApp &&
          this.state.error?.message &&
          this.state.error.message.toLowerCase().includes('notification')) {
        return <InAppBrowserScreen />;
      }
      return <ErrorScreen error={this.state.error?.message} onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

function GuestGuard({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.isGuest || user?.id === 'guest') {
      toast.error('يجب عليك تسجيل الدخول أولاً');
      navigate('/client', { replace: true });
    }
  }, [user, navigate]);

  if (user?.isGuest || user?.id === 'guest') {
    return null;
  }

  return children;
}

// ─────────────────────────────────────────────────────────────
//  BanGuard — يمنع المستخدمين الموقوفين من الوصول لأي صفحة داخلية
// ─────────────────────────────────────────────────────────────
function BanGuard({ children }) {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [banStatus, setBanStatus] = useState({
    checking: true,
    banned: false,
    reason: '',
    debtAmount: 0,
    pendingPayments: [],
  });

  useEffect(() => {
    let isMounted = true;

    const checkBanStatus = async () => {
      // لا نفحص الضيوف أو غير المسجلين
      if (!user?.id || user.id === 'guest' || user.isGuest) {
        if (isMounted) setBanStatus({ checking: false, banned: false, reason: '', debtAmount: 0, pendingPayments: [] });
        return;
      }

      // إذا كنا بالفعل في صفحة /banned لا نعيد الفحص
      if (location.pathname === '/banned') {
        if (isMounted) setBanStatus({ checking: false, banned: false, reason: '', debtAmount: 0, pendingPayments: [] });
        return;
      }

      try {
        // جلب بيانات المستخدم من الجدول المناسب
        let accountData = null;
        if (userType === 'provider') {
          accountData = await supabaseService.getProviderById(user.id);
        } else if (userType === 'client') {
          accountData = await supabaseService.getClientById(user.id);
        } else {
          // إذا لم نعرف النوع بعد، جرب الاثنين
          const [providerResult, clientResult] = await Promise.allSettled([
            supabaseService.getProviderById(user.id),
            supabaseService.getClientById(user.id),
          ]);
          accountData = providerResult.status === 'fulfilled' && providerResult.value
            ? providerResult.value
            : (clientResult.status === 'fulfilled' && clientResult.value ? clientResult.value : null);
        }

        if (!accountData) {
          if (isMounted) setBanStatus({ checking: false, banned: false, reason: '', debtAmount: 0, pendingPayments: [] });
          return;
        }

        const isActive = accountData.isActive ?? accountData.is_active ?? true;
        const banReason = accountData.banReason || accountData.ban_reason || '';

        if (isActive === false) {
          // 🆕 جلب إجمالي الديون المعلقة وقائمة الفواتير
          let debtAmount = 0;
          let pendingPayments = [];
          try {
            debtAmount = await supabaseService.getPendingCommissionsTotal(user.id);
            pendingPayments = await supabaseService.getPendingPayments(user.id);
          } catch (debtError) {
            console.error('❌ Error fetching debt info:', debtError);
          }

          if (isMounted) {
            setBanStatus({
              checking: false,
              banned: true,
              reason: banReason,
              debtAmount,
              pendingPayments,
            });
          }
          // توجيه إجباري إلى /banned
          navigate('/banned', { replace: true });
        } else {
          if (isMounted) setBanStatus({ checking: false, banned: false, reason: '', debtAmount: 0, pendingPayments: [] });
        }
      } catch (error) {
        console.error('❌ Ban check error:', error);
        // في حال الخطأ، نسمح بالوصول (لا نمنع المستخدم بسبب خطأ تقني)
        if (isMounted) setBanStatus({ checking: false, banned: false, reason: '', debtAmount: 0, pendingPayments: [] });
      }
    };

    checkBanStatus();

    return () => { isMounted = false; };
  }, [user?.id, userType, location.pathname, navigate]);

  // إذا كان المستخدم موقوفاً، اعرض شاشة الإيقاف مع بيانات الديون
  if (banStatus.banned) {
    return (
      <BannedScreen
        banReason={banStatus.reason}
        debtAmount={banStatus.debtAmount}
        pendingPayments={banStatus.pendingPayments}
      />
    );
  }

  return children;
}


function AuthChecker({ children }) {
  const { loading, error, isInitialized } = useAuth();
  const [localError, setLocalError] = useState(null);
  const [initTimeout, setInitTimeout] = useState(false);
  const location = useLocation();
  const { isOnline, isOfflineReady } = useOfflineSession();

  const isPublicRoute = location.pathname.startsWith('/provider-public/') ||
                        location.pathname.startsWith('/client-public/') ||
                        location.pathname === '/qr';

  useEffect(() => {
    const browserInfo = isInAppBrowser();
    if (browserInfo.isInApp) {
      console.log(`📱 Running in ${browserInfo.platform} in-app browser`);
      if (typeof Notification === 'undefined') {
        console.warn('⚠️ Notification API not available in this browser');
      }
    }
  }, []);

  useEffect(() => {
    if (error && !loading && error.includes('فشل تهيئة')) {
      setLocalError(error);
    } else {
      setLocalError(null);
    }
  }, [error, loading]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isInitialized && loading && !isPublicRoute) {
        console.warn('⚠️ Loading timeout exceeded');
        setInitTimeout(true);
      }
    }, 20000);
    return () => clearTimeout(timeout);
  }, [isInitialized, loading, isPublicRoute]);

  if (isPublicRoute) {
    console.log('🌐 PUBLIC ROUTE - Skipping AuthChecker');
    return children;
  }

  if (initTimeout) {
    return (
      <ErrorScreen
        error="انتهت مهلة التحميل. قد تكون هناك مشكلة في الاتصال بالخادم."
        onRetry={() => { setInitTimeout(false); window.location.reload(); }}
      />
    );
  }

  if ((loading || !isInitialized) && isOnline) return <LoadingScreen />;
  if (!isOnline && !isOfflineReady) return <LoadingScreen />;

  if (localError) {
    return (
      <ErrorScreen
        error={localError}
        onRetry={() => { setLocalError(null); window.location.reload(); }}
      />
    );
  }

  return children;
}

function AppRoutes() {
  const { user, userType, isInitialized } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCheckingGoogleAuth, setIsCheckingGoogleAuth] = useState(false);
  const { isOnline, offlineUser } = useOfflineSession();

  useEffect(() => {
    const checkGoogleAuthAndRedirect = async () => {
      if (isCheckingGoogleAuth || !user || userType) return;
      try {
        setIsCheckingGoogleAuth(true);
        const isGoogleOAuth = sessionStorage.getItem('google_oauth_initiated') === 'true';
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hasOAuthTokens = !!(accessToken && refreshToken);

        console.log('🔍 Google OAuth check:', {
          fromSessionStorage: isGoogleOAuth,
          hasTokensInURL: hasOAuthTokens,
          currentPath: location.pathname
        });

        if (!isGoogleOAuth && !hasOAuthTokens) {
          setIsCheckingGoogleAuth(false);
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 300));

        const checkWithTimeout = async () => {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Database check timeout')), 5000)
          );
          const checkPromise = Promise.allSettled([
            supabaseService.getProviderById(user.id),
            supabaseService.getClientById(user.id)
          ]);
          return Promise.race([checkPromise, timeoutPromise]);
        };

        let isProvider = false;
        let isClient = false;

        try {
          const [providerResult, clientResult] = await checkWithTimeout();
          isProvider = providerResult.status === 'fulfilled' && providerResult.value;
          isClient = clientResult.status === 'fulfilled' && clientResult.value;
        } catch (timeoutError) {
          console.warn('⚠️ Database check timeout, assuming new user');
        }

        sessionStorage.removeItem('google_oauth_initiated');
        if (hasOAuthTokens) window.history.replaceState(null, '', window.location.pathname);

        if (isProvider || isClient) {
  const targetRoute = isProvider ? '/provider' : '/client';
  toast.success('مرحباً بعودتك! تم تسجيل الدخول بنجاح');
  setTimeout(() => navigate(targetRoute, { replace: true }), 500);
} else {
  toast.success('مرحباً! يرجى إكمال معلومات التسجيل', { duration: 4000 });
  setTimeout(() => navigate('/register', { replace: true }), 500);
}
      } catch (error) {
        console.error('❌ Error checking Google auth:', error);
        sessionStorage.removeItem('google_oauth_initiated');
      } finally {
        setIsCheckingGoogleAuth(false);
      }
    };

    if (user) checkGoogleAuthAndRedirect();
  }, [user, userType, location.pathname, navigate, isCheckingGoogleAuth]);

  const handlePostLoginActions = async (currentUser) => {
    try {
      // التحقق من وجود صفحة مزود معلقة للعودة إليها
      const pendingProviderPage = sessionStorage.getItem('pending_provider_page');
      if (pendingProviderPage) {
        sessionStorage.removeItem('pending_provider_page');
        const [providerResult, clientResult] = await Promise.allSettled([
          supabaseService.getProviderById(currentUser.id),
          supabaseService.getClientById(currentUser.id)
        ]);
        const isProvider = providerResult.status === 'fulfilled' && providerResult.value;
        const isClient = clientResult.status === 'fulfilled' && clientResult.value;
        if (isProvider || isClient) {
          toast.success('مرحباً بعودتك! تم تسجيل الدخول بنجاح');
          setTimeout(() => navigate(pendingProviderPage, { replace: true }), 500);
          return;
        }
      }

      const pendingRequestStr = sessionStorage.getItem('pending_request');
      if (pendingRequestStr) {
        const pendingRequest = JSON.parse(pendingRequestStr);
        toast.loading('جاري إرسال الطلب...', { id: 'auto-request' });
        const newRequest = {
          clientId: currentUser.id,
          providerId: pendingRequest.providerId,
          serviceId: pendingRequest.serviceId,
          status: 'pending'
        };
        await supabaseService.createRequest(newRequest);
        const mockRequestForNotification = {
          id: 'pending',
          client: { fullName: currentUser.user_metadata?.full_name || currentUser.email || 'عميل' },
          service: { name: pendingRequest.serviceName },
          provider: { fullName: pendingRequest.providerName }
        };
        await notificationService.notifyNewRequest(pendingRequest.providerId, mockRequestForNotification);
        sessionStorage.removeItem('pending_request');
        setTimeout(() => navigate('/client'), 1000);
      }
    } catch (error) {
      console.error('❌ Error handling post-login action:', error);
      toast.error('حدث خطأ أثناء إرسال الطلب تلقائياً', { id: 'auto-request' });
      sessionStorage.removeItem('pending_request');
    }
  };

  useEffect(() => {
    if (user && user.id) {
      console.log('🔔 جاري تهيئة الإشعارات للمستخدم:', user.id);
      const browserInfo = isInAppBrowser();
      if (browserInfo.isInApp) {
        console.warn(`⚠️ Skipping notification initialization in ${browserInfo.platform} in-app browser`);
      } else {
        notificationService.initialize(user.id).catch(err => {
          console.error('❌ فشل تهيئة الإشعارات:', err);
        });
      }
    }
  }, [user?.id]);

  if (location.pathname.startsWith('/provider-public/') ||
      location.pathname.startsWith('/client-public/') ||
      location.pathname === '/qr') {
    console.log('🌐 Rendering PUBLIC ROUTE:', location.pathname);
    return (
      <>
        <Routes location={location}>
          <Route path="/provider-public/:providerId" element={<PageWrapper><ProviderPublicProfileScreen /></PageWrapper>} />
          <Route path="/client-public/:clientId" element={<PageWrapper><ClientPublicProfileScreen /></PageWrapper>} />
          <Route path="/qr" element={<PageWrapper><QrScanPage /></PageWrapper>} />
        </Routes>
        <GlobalToaster />
      </>
    );
  }

  if (!isInitialized) return <LoadingScreen />;

  const renderRoutes = () => {
    if (location.pathname === '/verify-recovery') {
      return (
        <Routes location={location}>
          <Route path="/verify-recovery" element={<PageWrapper><VerifyRecoveryScreen /></PageWrapper>} />
          <Route path="/*" element={<Navigate to="/verify-recovery" replace />} />
        </Routes>
      );
    }

    if (location.pathname === '/reset-password') {
      return (
        <Routes location={location}>
          <Route path="/reset-password" element={<PageWrapper><ResetPasswordScreen /></PageWrapper>} />
          <Route path="/*" element={<Navigate to="/reset-password" replace />} />
        </Routes>
      );
    }

    if (!user && !isOnline && offlineUser) {
      console.log('📴 Offline mode — user identified from cache');
      const cachedUserType = offlineUser?.user_metadata?.user_type;
      const mainRoute = cachedUserType === 'provider' ? '/provider' : '/client';
      return (
        <Routes location={location}>
          <Route path="/client" element={<ClientMainScreen />} />
          <Route path="/provider" element={<ProviderMainScreen />} />
          <Route path="/chat/:conversationId" element={<ChatScreen />} />
          <Route path="*" element={<Navigate to={mainRoute} replace />} />
        </Routes>
      );
    }

    if (!user) {
      console.log('🔓 Unauthenticated User - Guest Access');
      return (
        <Routes location={location}>
          <Route path="/auth" element={<PageWrapper><AuthScreen /></PageWrapper>} />
          <Route path="/forgot-password" element={<PageWrapper><ForgotPasswordScreen /></PageWrapper>} />
          <Route path="/verify-email" element={<PageWrapper><VerifyEmailScreen /></PageWrapper>} />
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      );
    }

    if (user && !userType) {
      console.log('📝 Registered but no UserType');
      if (isCheckingGoogleAuth) return <LoadingScreen />;
      return (
        <Routes location={location}>
          <Route path="/verify" element={<PageWrapper><AuthVerificationScreen /></PageWrapper>} />
          <Route path="/register" element={<PageWrapper><RegistrationScreen /></PageWrapper>} />
          <Route path="/auth" element={<PageWrapper><AuthScreen /></PageWrapper>} />
          <Route path="*" element={<Navigate to="/register" replace />} />
        </Routes>
      );
    }

    if (user && userType) {
  console.log('✅ Fully Authenticated User');
  const mainRoute = userType === 'provider' ? '/provider' : '/client';

  // 🆕 التحقق من وجود صفحة مزود معلقة للعودة إليها — فقط للزبون
  const pendingProviderPage = sessionStorage.getItem('pending_provider_page');
  if (pendingProviderPage) {
    if (userType === 'client' && location.pathname !== pendingProviderPage) {
      sessionStorage.removeItem('pending_provider_page');
      return <Navigate to={pendingProviderPage} replace />;
    }
    if (userType !== 'client') {
      // المستخدم مزود، لا داعٍ للتحويل — احذف الصفحة المعلقة
      sessionStorage.removeItem('pending_provider_page');
    }
  }

  return (
    <Routes location={location}>
          <Route path="/" element={<Navigate to={mainRoute} replace />} />
          <Route path="/client" element={<ClientMainScreen />} />
          <Route path="/provider" element={<ProviderMainScreen />} />
          <Route path="/provider/profile" element={<ProviderProfileScreen />} />
          <Route path="/provider/conversations" element={<ProviderConversationsScreen />} />
          <Route path="/provider/bank-account" element={<ProviderBankAccountScreen />} />
          
          {/* ✅ المسارات الموحدة للبطاقة والمحفظة */}
          <Route path="/member-card" element={<MemberCardScreen />} />
          <Route path="/wallet" element={<WalletScreen />} />
          <Route path="/wallet/withdraw" element={<WithdrawalRequestScreen />} />
          <Route path="/wallet/transactions" element={<WalletTransactionsScreen />} />
          <Route path="/wallet/recharge-receipt" element={<RechargeReceiptScreen />} />
          
          <Route path="/test-public-profile" element={<TestPublicProfileScreen />} />
          <Route path="/fcm-token-display" element={<FCMTokenDisplay />} />
          <Route path="/chat/:conversationId" element={<ChatScreen />} />
          <Route path="/providers" element={<ProvidersListScreen />} />
          <Route path="/payment-method" element={<PaymentMethodScreen />} />
          <Route path="/card-payment" element={<CardPaymentScreen />} />
          <Route path="/receipt-upload" element={<ReceiptUploadScreen />} />
          <Route path="/payment-success" element={<PaymentSuccessScreen />} />
          <Route path="/memory-game" element={<PageWrapper><GamesHub /></PageWrapper>} />
          <Route path="/my-library"              element={<PageWrapper><GuestGuard><MyLibraryScreen /></GuestGuard></PageWrapper>} />
<Route path="/digital-store"           element={<PageWrapper><GuestGuard><DigitalProductsScreen /></GuestGuard></PageWrapper>} />
<Route path="/digital-product-detail"  element={<PageWrapper><GuestGuard><DigitalProductDetailScreen /></GuestGuard></PageWrapper>} />

{/* ✅ متجر خدمة الجديد */}
<Route path="/store"                   element={<PageWrapper><GuestGuard><StoreScreen /></GuestGuard></PageWrapper>} />
<Route path="/store/product/:id"       element={<PageWrapper><GuestGuard><StoreProductDetailScreen /></GuestGuard></PageWrapper>} />
<Route path="/store/cart"              element={<PageWrapper><GuestGuard><StoreCartScreen /></GuestGuard></PageWrapper>} />

          {/* ✅ متجر المأكولات (Khidma Food) */}
<Route path="/food-store"              element={<PageWrapper><GuestGuard><FoodStoreScreen /></GuestGuard></PageWrapper>} />
<Route path="/food-store/product/:id"  element={<PageWrapper><GuestGuard><FoodProductDetailScreen /></GuestGuard></PageWrapper>} />
<Route path="/food-store/cart"         element={<PageWrapper><GuestGuard><FoodCartScreen /></GuestGuard></PageWrapper>} />

{/* ✅ المحفظة الشخصية QR والتحويل */}
<Route path="/personal-qr"             element={<PageWrapper><PersonalQrScreen /></PageWrapper>} />
<Route path="/qr-transfer/:walletCode" element={<PageWrapper><QrTransferScreen /></PageWrapper>} />

<Route path="*" element={<Navigate to={mainRoute} replace />} />
        </Routes>
      );
    }

    return <LoadingScreen />;
  };

  return (
    <>
      {!isOnline && (
        <div style={{
          background: '#d97706', color: 'white', textAlign: 'center',
          padding: '8px 16px', fontSize: '13px', fontFamily: 'Tajawal, sans-serif',
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, direction: 'rtl'
        }}>
          📴 أنت غير متصل بالإنترنت — تعرض بيانات محفوظة فقط
        </div>
      )}
      {renderRoutes()}
      <IncomingCallManager />
      <GlobalToaster />
    </>
  );
}

function GlobalToaster() {
  return (
    <Toaster
      position="top-center"
      gutter={16}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#1f2937', color: '#fff', textAlign: 'right',
          fontFamily: 'Tajawal, sans-serif', borderRadius: '12px',
          padding: '16px', fontSize: '14px', maxWidth: '500px',
        },
        success: {
          iconTheme: { primary: '#10b981', secondary: '#fff' },
          style: { background: '#065f46' },
        },
        error: {
          iconTheme: { primary: '#ef4444', secondary: '#fff' },
          style: { background: '#7f1d1d' },
        },
        loading: { style: { background: '#1e40af' } },
      }}
    />
  );
}

function App() {
  const [shouldShowInAppWarning, setShouldShowInAppWarning] = useState(false);

  useEffect(() => {
    const browserInfo = isInAppBrowser();
    if (browserInfo.isInApp) {
      console.log(`🚨 App running in ${browserInfo.platform} in-app browser`);
      if (typeof Notification === 'undefined') setShouldShowInAppWarning(true);
    }
  }, []);

  if (shouldShowInAppWarning) return <InAppBrowserScreen />;

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* ✅ معزولة تماماً — لا تمر بأي provider أو checker */}
          <Route
            path="/read"
            element={<ReadBookScreen />}
          />
          <Route
            path="/contact-us"
            element={<ContactUsScreen />}
          />
          <Route
            path="/rules-and-terms"
            element={<RulesAndTermsScreen />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicyScreen />}
          />
          <Route
            path="/delete-account"
            element={
              <AuthProvider>
                <DeleteAccountScreen />
              </AuthProvider>
            }
          />

          {/* ✅ صفحة الإيقاف — تتطلب AuthProvider لزر تسجيل الخروج */}
          <Route
            path="/banned"
            element={
              <AuthProvider>
                <BannedScreen />
              </AuthProvider>
            }
          />

          {/* ✅ باقي التطبيق — مع حماية الإيقاف */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <AuthChecker>
                  <BanGuard>
                    <AppRoutes />
                  </BanGuard>
                </AuthChecker>
              </AuthProvider>
            }
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
